import React, { useCallback, useMemo } from "react";
import { usePermissions } from "../hooks/usePermissions";
import { MODE_CAPABILITY_MAP, canAccessMode } from "../helpers/screenGuards";
import { CAPABILITY } from "../helpers/permissions";
import { actorCanAccessGoogleVisibility } from "../helpers/googleVisibilityAccess";
import { actorCanAccessFeedbackInbox } from "../feedback/feedbackInboxAccess";
import { isGuestActor } from "../helpers/guestMode";
import { isBranchMapPreviewHost, openBranchMap } from "../helpers/branchMapAccess";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "./CrewPilotNavRow";

/**
 * "More" — business/operational/reporting modules that don't already have a
 * persistent bottom-navigation destination. Command Center and
 * Administration are intentionally not listed here: their non-redundant
 * content (Time Logs) is reachable elsewhere (Home, this menu), and
 * organization configuration now lives under Settings. Clients and Estimates
 * are workspaces. Invoices, receivables, payables, and payroll are reached
 * from Finance. Mission Control and Play Store Readiness stay on the desktop
 * sidebar and their existing entry cards.
 */
const MODE_CATEGORIES = [
  {
    id: "workspaces",
    label: "Workspaces",
    modules: [
      { mode: "clients", title: "Clients", icon: "emoji", emoji: "👤" },
      { mode: "estimates", title: "Estimates", icon: "emoji", emoji: "📋" },
    ],
  },
  {
    id: "reporting_analytics",
    label: "Reporting & Analytics",
    modules: [
      { mode: "intelligence", title: "Reports & Analytics", icon: "doc" },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    modules: [
      { mode: "finance", title: "Finance", icon: "emoji", emoji: "🏦" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    modules: [
      { mode: "materials", title: "Materials & Services", icon: "emoji", emoji: "📦" },
      { mode: "tasks", title: "Tasks", icon: "emoji", emoji: "✅" },
      { mode: "planning", title: "Planning", icon: "emoji", emoji: "🗓️" },
      {
        mode: "manual_time_entry",
        title: "Time Logs",
        icon: "emoji",
        emoji: "🕒",
      },
    ],
  },
  {
    id: "support",
    label: "Support",
    modules: [
      { mode: "feedback", title: "Send Feedback", icon: "emoji", emoji: "💬" },
    ],
  },
  {
    id: "internal_tools",
    label: "Internal Tools",
    modules: [
      {
        mode: "google_visibility",
        title: "Google Visibility",
        icon: "emoji",
        emoji: "🔎",
      },
      {
        mode: "feedback_inbox",
        title: "Feedback Inbox",
        icon: "emoji",
        emoji: "📥",
      },
      {
        mode: "git_branch_map",
        title: "Git Branch Map",
        icon: "emoji",
        emoji: "🗺️",
      },
    ],
  },
];

function ModuleIcon({ module }) {
  if (module.icon === "emoji") {
    return <span style={{ color: module.emojiColor }}>{module.emoji}</span>;
  }

  if (module.icon === "mission") {
    return (
      <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="6" width="20" height="20" rx="10" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="1.8" />
        <line x1="16" y1="4" x2="16" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="16" y1="23" x2="16" y2="28" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="4" y1="16" x2="9" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="23" y1="16" x2="28" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="4" width="20" height="24" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <line x1="11" y1="11" x2="21" y2="11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="11" y1="16" x2="21" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="11" y1="21" x2="17" y2="21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * ModeSelectScreen
 * Work-area launcher after auth — modules filtered by permissions.
 */
export function ModeSelectScreen({ onSelectMode, onOpenDashboard }) {
  const { can, actor, orgPolicies } = usePermissions();

  const isModuleVisible = useCallback(
    (mode) => {
      if (mode === "google_visibility") {
        return actorCanAccessGoogleVisibility(actor);
      }
      if (mode === "feedback_inbox") {
        return actorCanAccessFeedbackInbox(actor);
      }
      if (mode === "git_branch_map") {
        return isBranchMapPreviewHost();
      }
      if (mode === "feedback") {
        return Boolean(actor) && !isGuestActor(actor);
      }
      if (mode === "finance") {
        return canAccessMode(actor, "finance", orgPolicies);
      }
      const capability = MODE_CAPABILITY_MAP[mode];
      return capability ? can(capability) : false;
    },
    [actor, can, orgPolicies],
  );

  const visibleCategories = useMemo(
    () =>
      MODE_CATEGORIES.map((category) => ({
        ...category,
        modules: category.modules.filter((module) => isModuleVisible(module.mode)),
      })).filter((category) => category.modules.length > 0),
    [isModuleVisible],
  );

  const dashboardAllowed = can(CAPABILITY.SCREEN_KPI_DASHBOARD);

  return (
    <div className="mode-select-screen nx-mode-screen">
      <div
        style={{
          background: "var(--nx-surface)",
          borderBottom: "1px solid var(--nx-border)",
          padding: "18px 20px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        <button
          type="button"
          className="cockpit-back-btn guide-module-back ios-interaction-guard"
          onClick={onOpenDashboard}
          disabled={!dashboardAllowed}
          style={dashboardAllowed ? undefined : { opacity: 0.55, cursor: "default" }}
        >
          Back
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "5px 12px",
            borderRadius: 20,
            border: "1px solid rgba(78,226,161,0.3)",
            background: "rgba(78,226,161,0.05)",
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--nx-green)",
              animation: "nx-pulse-dot 2s ease infinite",
            }}
          />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              letterSpacing: "1.5px",
              color: "var(--nx-green)",
              textTransform: "uppercase",
            }}
          >
            READY
          </span>
        </div>
      </div>

      <div style={{ padding: "22px 20px 0" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--nx-text-3)",
            borderLeft: "2px solid var(--nx-gold)",
            paddingLeft: 10,
            marginBottom: 16,
          }}
        >
          Select Module
        </div>
      </div>

      <div className="cp-nav-screen-body">
        {visibleCategories.map((category) => (
          <React.Fragment key={category.id}>
            <CrewPilotNavSectionHeader>{category.label}</CrewPilotNavSectionHeader>
            <CrewPilotNavList>
              {category.modules.map((module) => (
                <CrewPilotNavRow
                  key={module.mode}
                  icon={<ModuleIcon module={module} />}
                  title={module.title}
                  onClick={() =>
                    module.mode === "git_branch_map" ? openBranchMap() : onSelectMode(module.mode)
                  }
                />
              ))}
            </CrewPilotNavList>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
